import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { configuredUnitPrice, money } from "../lib/pos";

/* ---------- Item Configurator (variants + sub-variants + addons) ---------- */
export function ItemConfigurator({ item, onCancel, onAdd }) {
  const hasVariants = (item.variants || []).length > 0;
  // When the item has multiple variants, hide "Base only" — the customer must pick one.
  // Keep it for single-variant items so the base price stays orderable.
  const allowBaseOnly = (item.variants || []).length <= 1;
  const [variant, setVariant] = useState(() => (hasVariants && !allowBaseOnly ? item.variants[0] : null));
  const [subVariantIds, setSubVariantIds] = useState({}); // {[svId]: true}
  const [addonQtys, setAddonQtys] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");

  const availableAddons = (item.addons || []).filter((a) => {
    const linked = (a.variant_ids || []);
    if (linked.length === 0) return true; // independent add-on always available
    return variant && linked.includes(variant.id);
  });

  const availableSubVariants = variant ? (variant.sub_variants || []) : [];

  // Reset addon selections + sub-variant selections that aren't available when variant changes
  useEffect(() => {
    setAddonQtys((prev) => {
      const next = {};
      for (const a of availableAddons) {
        if (prev[a.id]) next[a.id] = prev[a.id];
      }
      return next;
    });
    setSubVariantIds({});
  }, [variant?.id]);

  const chosenSubVariants = availableSubVariants.filter((sv) => subVariantIds[sv.id]);
  const composedVariant = variant ? { ...variant, sub_variants: chosenSubVariants } : null;

  const addons = availableAddons
    .filter((a) => (addonQtys[a.id] || 0) > 0)
    .map((a) => ({ id: a.id, name: a.name, price: a.price, quantity: addonQtys[a.id] }));
  const unitPrice = configuredUnitPrice(item.price, composedVariant, addons);

  const bump = (id, delta) => setAddonQtys((prev) => {
    const cur = prev[id] || 0;
    const next = Math.max(0, Math.min(9, cur + delta));
    const cp = { ...prev }; if (next === 0) delete cp[id]; else cp[id] = next;
    return cp;
  });

  const toggleSub = (svId) => setSubVariantIds((prev) => {
    const cp = { ...prev };
    if (cp[svId]) delete cp[svId]; else cp[svId] = true;
    return cp;
  });

  return (
    <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && onCancel()}>
      <div className="modal configurator" style={{ width: "min(520px, 92vw)" }}>
        <div className="section-line">
          <h2>{item.name}</h2>
          <button data-testid="configurator-close-button" onClick={onCancel}>×</button>
        </div>
        {item.description && <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>{item.description}</p>}

        {hasVariants && (
          <>
            <p className="eyebrow" style={{ marginTop: 14 }}>VARIANT {allowBaseOnly ? <span style={{ color: "var(--muted)", letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· optional</span> : <span style={{ color: "var(--gold)", letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· required</span>}</p>
            <div className="variant-list">
              {allowBaseOnly && (
                <button
                  data-testid="variant-none-button"
                  className={`variant-row ${variant === null ? "on" : ""}`}
                  onClick={() => setVariant(null)}
                >
                  <span className="v-radio" />
                  <b>Base only</b>
                  <em>no variant</em>
                </button>
              )}
              {item.variants.map((v) => (
                <button data-testid={`variant-${v.id}-button`} key={v.id}
                  className={`variant-row ${variant?.id === v.id ? "on" : ""}`}
                  onClick={() => setVariant(v)}>
                  <span className="v-radio" />
                  <b>{v.name}{(v.sub_variants || []).length > 0 && <small style={{ marginLeft: 6, color: "var(--muted)", fontWeight: 400, fontSize: 10 }}>· {v.sub_variants.length} option{v.sub_variants.length > 1 ? "s" : ""}</small>}</b>
                  <em>{v.price_delta > 0 ? `+${money(v.price_delta)}` : v.price_delta < 0 ? `${money(v.price_delta)}` : "included"}</em>
                </button>
              ))}
            </div>
          </>
        )}

        {availableSubVariants.length > 0 && (
          <>
            <p className="eyebrow" style={{ marginTop: 14 }}>SUB-VARIANTS <span style={{ color: "var(--muted)", letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· pick any · added to price</span></p>
            <div className="sub-variant-list" data-testid="sub-variant-list">
              {availableSubVariants.map((sv) => {
                const on = !!subVariantIds[sv.id];
                return (
                  <button
                    key={sv.id}
                    data-testid={`sub-variant-${sv.id}-button`}
                    className={`sub-variant-row ${on ? "on" : ""}`}
                    onClick={() => toggleSub(sv.id)}
                  >
                    <span className="sv-check">{on ? "✓" : ""}</span>
                    <b>{sv.name}</b>
                    <em>{sv.price_delta > 0 ? `+${money(sv.price_delta)}` : sv.price_delta < 0 ? `${money(sv.price_delta)}` : "included"}</em>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {availableAddons.length > 0 && (
          <>
            <p className="eyebrow" style={{ marginTop: 14 }}>ADD-ONS {hasVariants && <span style={{ color: "var(--muted)", letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· for {variant?.name || "any"}</span>}</p>
            <div className="addon-list">
              {availableAddons.map((a) => {
                const qty = addonQtys[a.id] || 0;
                return (
                  <div className="addon-row" key={a.id}>
                    <div>
                      <b>{a.name}</b>
                      <small>+{money(a.price)} each{(a.variant_ids || []).length === 0 ? " · always" : ""}</small>
                    </div>
                    <div className="stepper">
                      <button data-testid={`addon-${a.id}-minus`} onClick={() => bump(a.id, -1)} disabled={qty === 0}><Minus size={13} /></button>
                      <span>{qty}</span>
                      <button data-testid={`addon-${a.id}-plus`} onClick={() => bump(a.id, +1)}><Plus size={13} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="configurator-total">
          <span>Unit price</span>
          <b data-testid="configurator-total">{money(unitPrice)}</b>
        </div>

        <div className="qty-note-row">
          <div>
            <p className="eyebrow" style={{ marginBottom: 6 }}>QUANTITY</p>
            <div className="stepper stepper-lg" data-testid="configurator-quantity">
              <button data-testid="configurator-qty-minus" onClick={() => setQuantity((q) => Math.max(1, q - 1))} disabled={quantity <= 1}><Minus size={16} /></button>
              <span>{quantity}</span>
              <button data-testid="configurator-qty-plus" onClick={() => setQuantity((q) => Math.min(99, q + 1))}><Plus size={16} /></button>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <p className="eyebrow" style={{ marginBottom: 6 }}>NOTE <span style={{ color: "var(--muted)", letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· optional</span></p>
            <input
              data-testid="configurator-note-input"
              type="text"
              className="configurator-note"
              placeholder="e.g. no chilli, extra crispy"
              value={note}
              maxLength={140}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        <button
          data-testid="configurator-add-button"
          className="primary wide"
          disabled={hasVariants && !allowBaseOnly && !variant}
          title={hasVariants && !allowBaseOnly && !variant ? "Please select a variant" : ""}
          onClick={() => onAdd({ variant: composedVariant, addons, unitPrice, quantity, note: note.trim() || null })}
        >
          {hasVariants && !allowBaseOnly && !variant ? "Select a variant" : `Add ${quantity}× · ${money(unitPrice * quantity)}`}
        </button>
      </div>
    </div>
  );
}
